<template>
  <div class="w-full h-full relative">
    <!-- Map container -->
    <div class="w-full h-full relative z-0">
      <slot></slot>
    </div>

    <!-- Top search bar -->
    <div class="mobile-margin-top absolute top-10 left-0 right-0 z-[11] p-4">
      <form @submit.prevent="confirmSelection">
        <div class="relative group max-w-xl mx-auto z-30">
          <div class="relative">
            <div
              class="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                ></path>
              </svg>
            </div>
            <input
              @input="onSearchInput(query)"
              @keydown.enter.prevent="searchOnce"
              v-model="query"
              type="search"
              name="user_login"
              autocomplete="off"
              placeholder="Rechercher une adresse..."
              class="w-full pl-12 pr-12 py-4 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
            />
            <div class="absolute right-4 top-1/2 transform -translate-y-1/2">
              <div
                v-if="searchingNomination"
                class="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"
              ></div>
            </div>
          </div>

          <!-- Suggestions dropdown -->
          <div class="absolute w-full top-full mt-2 z-[2]">
            <div
              v-if="suggestions.length || searchingNomination"
              class="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-xl max-h-80 overflow-auto opacity-0 group-focus-within:opacity-100 pointer-events-none group-focus-within:pointer-events-auto transition-all duration-200"
            >
              <li
                v-for="(s, i) in suggestions"
                :key="i"
                @click="selectSuggestion(s)"
                class="list-none px-4 py-3 hover:bg-gray-50 cursor-pointer text-gray-800 border-b border-gray-100 last:border-b-0 transition-colors duration-150"
                tabindex="-1"
              >
                <div class="flex items-start gap-3">
                  <div
                    class="w-5 h-5 bg-primary rounded-full flex-shrink-0 mt-0.5 flex items-center justify-center"
                  >
                    <svg
                      class="w-3 h-3 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="font-medium text-gray-900 truncate">
                      {{ s.display_name }}
                    </div>
                  </div>
                </div>
              </li>
              <li
                v-if="searchingNomination"
                class="list-none px-4 py-3 text-gray-500 text-center"
              >
                <div class="flex items-center justify-center gap-2">
                  <div
                    class="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"
                  ></div>
                  Recherche en cours...
                </div>
              </li>
            </div>
          </div>
        </div>
      </form>
    </div>
    <!-- Back button -->
    <div class="absolute top-32 left-4 z-10 mobile-margin-top">
      <button
        @click="emit('backClicked')"
        class="w-12 h-12 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-lg hover:bg-white hover:shadow-xl transition-all duration-200 group flex items-center justify-center"
      >
        <svg
          class="w-6 h-6 text-slate-700 group-hover:text-primary transition-colors"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="3"
            d="M15 19l-7-7 7-7"
          ></path>
        </svg>
      </button>
    </div>

    <!-- Current location button -->
    <div class="absolute top-32 right-4 z-10 mobile-margin-top">
      <button
        @click="handleUseCurrentLocation"
        class="p-4 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-lg hover:bg-white hover:shadow-xl transition-all duration-200 group"
      >
        <div class="flex items-center gap-2">
          <svg
            class="w-5 h-5 text-primary group-hover:text-primary/80 transition-colors"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            ></path>
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            ></path>
          </svg>
          <span class="text-sm font-medium text-gray-700">Ma position</span>
        </div>
      </button>
    </div>

    <!-- Bottom details panel -->
    <div
      class="absolute bottom-0 left-0 right-0 z-10 bg-white/95 backdrop-blur-sm border-t border-gray-200 shadow-2xl"
    >
      <div class="p-4 max-w-md mx-auto max-h-[60vh] overflow-y-auto">
        <!-- Additional Fields -->
        <div
          v-if="additionalFields && additionalFields.length > 0"
          class="space-y-3 mb-4"
        >
          <div v-for="(field, index) in additionalFields" :key="index">
            <label class="block text-sm font-semibold text-gray-700 mb-2">
              <span v-if="field.required" class="text-red-500 mr-1">*</span>
              {{ field.label }}
              <span v-if="!field.required" class="text-gray-400 text-xs"
                >(optionnel)</span
              >
            </label>

            <!-- Text input -->
            <input
              v-if="field.type === 'text' || field.type === 'tel'"
              v-model="fieldValues[field.key]"
              :type="field.type || 'text'"
              :placeholder="field.placeholder || ''"
              :required="field.required"
              :class="getFieldClass(field)"
            />

            <!-- Textarea -->
            <textarea
              v-else-if="field.type === 'textarea'"
              v-model="fieldValues[field.key]"
              rows="2"
              :placeholder="field.placeholder || ''"
              :required="field.required"
              :class="getFieldClass(field)"
            ></textarea>
          </div>
        </div>

        <!-- Description field -->
        <div class="mb-4">
          <label class="block text-sm font-semibold text-gray-700 mb-2">
            <span class="text-red-500 mr-1">*</span>
            <svg
              class="w-4 h-4 inline-block mr-1 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              ></path>
            </svg>
            Précisions sur l'adresse
          </label>
          <textarea
            v-model="description"
            rows="2"
            placeholder="Porte, étage, bâtiment, instructions spéciales..."
            class="w-full px-4 py-3 bg-white border rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 resize-none"
            :class="description.trim() ? 'border-gray-200 focus:ring-primary focus:border-transparent' : 'border-red-300 focus:ring-red-500 focus:border-red-500'"
          ></textarea>
        </div>

        <!-- Confirm button -->
        <button
          @click="handleConfirmClick"
          :disabled="resolvingAddress"
          class="w-full py-4 bg-gradient-to-r from-primary to-primary/90 text-white font-semibold rounded-xl hover:from-primary/90 hover:to-primary disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
        >
          <div class="flex items-center justify-center gap-2">
            <svg
              v-if="!resolvingAddress"
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              ></path>
            </svg>
            <div
              v-if="resolvingAddress"
              class="animate-spin rounded-full h-5 w-5 border-b-2 border-white"
            ></div>
            <span v-if="resolvingAddress">Récupération de l'adresse...</span>
            <span v-else>Confirmer cette position</span>
          </div>
        </button>

        <!-- Selected location info -->
        <div
          v-if="selectedLocation?.address"
          class="mt-3 px-2 py-2 bg-primary/5 rounded-lg border border-primary/20"
        >
          <div class="flex items-center gap-2">
            <svg
              class="w-3.5 h-3.5 text-primary flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                clip-rule="evenodd"
              />
            </svg>
            <div class="flex-1 min-w-0">
              <div class="text-primary/80 truncate font-bold">
                {{ selectedLocation.address }}
              </div>
            </div>
          </div>
        </div>
        <div class="mobile-padding"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { bus } from '@core/events/eventSystem'
import constants from '@core/utils/constants'

const props = defineProps({
  onSearchInput: { type: Function },
  searchOnce: { type: Function },
  suggestions: {
    type: Array,
    default() {
      return []
    },
  },
  selectSuggestion: { type: Function },
  useCurrentLocation: { type: Function },
  confirmSelection: { type: Function },
  searchingNomination: { type: Boolean },
  resolvingAddress: { type: Boolean },
  selectedLocation: Object,
  selectedCity: String,
  additionalFields: {
    type: Array,
    default() {
      return []
    },
  },
  defaultValues: {
    type: Object,
    default() {
      return {}
    },
  },
  autoUseCurrentLocation: {
    type: Boolean,
    default: false,
  },
})

const handleUseCurrentLocation = async () => {
  await props.useCurrentLocation()
  prefillUserDefaultFields()
}

function prefillUserDefaultFields() {
  // Auto-fill additional fields with userDefaultValue if they are currently empty
  if (props.additionalFields && props.additionalFields.length > 0) {
    props.additionalFields.forEach((field) => {
      const currentValue = fieldValues.value[field.key]
      if (
        field.userDefaultValue &&
        (!currentValue || (typeof currentValue === 'string' && currentValue.trim() === ''))
      ) {
        fieldValues.value[field.key] = field.userDefaultValue
      }
    })
  }
}

const emit = defineEmits(['fieldValuesChanged', 'backClicked'])

const query = ref('')
const description = ref(props.defaultValues?.description || '')
const city = ref('')

const fieldValues = ref({})

// Initialize fieldValues with defaultValues (excluding description which is handled separately)
if (props.defaultValues && Object.keys(props.defaultValues).length > 0) {
  Object.keys(props.defaultValues).forEach(key => {
    if (key !== 'description') {
      fieldValues.value[key] = props.defaultValues[key]
    }
  })
}

watch(
  () => props.additionalFields,
  fields => {
    if (fields && fields.length > 0) {
      fields.forEach(field => {
        if (fieldValues.value[field.key] === undefined) {
          fieldValues.value[field.key] = field.defaultValue || ''
        }
      })
    }
    emit('fieldValuesChanged', fieldValues.value)
  },
  { immediate: true, deep: true },
)

watch(
  fieldValues,
  newVal => {
    emit('fieldValuesChanged', newVal)
  },
  { deep: true },
)

// Quand l'init automatique déclenche "ma position", pré-remplir les champs
watch(
  () => props.autoUseCurrentLocation,
  (val) => {
    if (val) {
      prefillUserDefaultFields()
    }
  },
)

watch(
  () => props.selectedCity,
  newVal => {
    city.value = newVal || ''
  },
  { immediate: true, deep: true },
)

watch(
  () => props.selectedLocation,
  newVal => {
    query.value = newVal?.address || ''
  },
  { immediate: true, deep: true },
)

const areRequiredFieldsFilled = computed(() => {
  if (!props.additionalFields || props.additionalFields.length === 0) {
    return true
  }

  for (const field of props.additionalFields) {
    if (field.required) {
      const value = fieldValues.value[field.key]
      if (!value || value.trim() === '') {
        return false
      }
    }
  }

  return true
})

const handleConfirmClick = () => {
  if (!props.selectedLocation?.lat || !props.selectedLocation?.lng) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'warning',
      title: 'Position requise',
      body: 'Veuillez sélectionner une position sur la carte',
    })
    return
  }

  // if (!description.value || description.value.trim() === '') {
  //   bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
  //     type: 'warning',
  //     title: 'Champ requis',
  //     body: 'Veuillez remplir les précisions sur l\'adresse',
  //   })
  //   return
  // }

  if (!areRequiredFieldsFilled.value) {
    const missingFields = props.additionalFields
      .filter(
        f =>
          f.required &&
          (!fieldValues.value[f.key] || fieldValues.value[f.key].trim() === ''),
      )
      .map(f => f.label)

    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'warning',
      title: 'Champs requis',
      body: `Veuillez remplir: ${missingFields.join(', ')}`,
    })
    return
  }

  props.confirmSelection(description.value)
}

const getFieldClass = field => {
  const isEmpty =
    field.required &&
    (!fieldValues.value[field.key] ||
      fieldValues.value[field.key].trim() === '')
  const baseClass =
    'w-full px-4 py-3 bg-white border rounded-xl focus:outline-none focus:ring-2 transition-all duration-200'

  if (isEmpty) {
    return `${baseClass} border-red-300 focus:ring-red-500 focus:border-red-500`
  }

  return `${baseClass} border-gray-200 focus:ring-primary focus:border-transparent`
}

defineExpose({
  fieldValues,
  areRequiredFieldsFilled,
  getFieldClass,
})
</script>

<style scoped>
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.group-focus-within > div {
  animation: slideIn 0.2s ease-out;
}

.overflow-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-auto::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
}

.overflow-auto::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 3px;
}

.overflow-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.5);
}

.mobile-margin-top {
  margin-top: env(safe-area-inset-top);
}

.mobile-padding {
  padding-bottom: env(safe-area-inset-bottom);
}
</style>
